/**
 * AmbientBackground — the page-level mood.
 *
 * Renders three slowly-drifting radial gradients ("orbs") behind the page
 * content, with subtle grid lines + vignette on top. Gives the app a
 * Framer/Vercel-style depth without needing WebGL.
 *
 * Drop this once at the app root — it's fixed to the viewport and marked
 * pointer-events: none so it never intercepts clicks.
 *
 * Usage:
 *   <AmbientBackground />        // default indigo/blue mood
 *   <AmbientBackground mood="money" />   // emerald-tinted for /financial
 */

import { motion } from 'framer-motion'

// Brand-kit rule: the ambient aurora stays in the Pinnacle blue family.
// Every "mood" reuses the same three-orb blue palette so no off-brand
// tint (violet, amber, green) ever bleeds into the app background.
const MOODS = {
  default: {
    orbs: [
      { color: '#3B82F6', size: 900, x: '-10%', y: '-20%', delay: 0 },
      { color: '#6BC0F0', size: 700, x: '85%', y: '30%', delay: 3 },
      { color: '#22D3EE', size: 600, x: '40%', y: '95%', delay: 6 },
    ],
  },
  money: {
    orbs: [
      { color: '#60A5FA', size: 900, x: '-10%', y: '-20%', delay: 0 },
      { color: '#3B82F6', size: 700, x: '85%', y: '30%', delay: 3 },
      { color: '#22D3EE', size: 600, x: '40%', y: '95%', delay: 6 },
    ],
  },
  warn: {
    orbs: [
      { color: '#F59E0B', size: 900, x: '-10%', y: '-20%', delay: 0 },
      { color: '#3B82F6', size: 700, x: '85%', y: '30%', delay: 3 },
      { color: '#6BC0F0', size: 600, x: '40%', y: '95%', delay: 6 },
    ],
  },
}

export default function AmbientBackground({ mood = 'default' }) {
  const cfg = MOODS[mood] || MOODS.default
  return (
    <div
      aria-hidden
      data-ambient-bg
      className="fixed inset-0 z-0 pointer-events-none overflow-hidden"
    >
      {/* base black */}
      <div className="absolute inset-0 bg-navy-900" />

      {/* subtle grid lines */}
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          backgroundPosition: 'center center',
          maskImage:
            'radial-gradient(ellipse at center, rgba(0,0,0,1) 30%, transparent 85%)',
        }}
      />

      {/* drifting gradient orbs */}
      {cfg.orbs.map((o, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: o.size,
            height: o.size,
            left: o.x,
            top: o.y,
            background: `radial-gradient(circle, ${o.color}32 0%, ${o.color}00 65%)`,
            filter: 'blur(60px)',
            willChange: 'transform',
          }}
          animate={{
            x: [0, 40, -20, 10, 0],
            y: [0, -25, 30, -10, 0],
            scale: [1, 1.08, 0.95, 1.04, 1],
          }}
          transition={{
            duration: 30 + i * 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: o.delay,
          }}
        />
      ))}

      {/* vignette — darker at edges, centers attention */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)',
        }}
      />

      {/* grain noise — keeps gradients from banding */}
      <div
        className="absolute inset-0 opacity-[0.025] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
    </div>
  )
}
